import axios, { AxiosError, AxiosProgressEvent, AxiosRequestConfig, CancelTokenSource } from 'axios';
import { redirectToPublicPage } from 'utils/gql/redirectToPublicPage';
import { refreshAccessToken } from 'utils/gql/refreshAccessToken';
import { isSessionForCodeActive } from 'utils/authSessionForCode/isSessionForCodeActive';

export const axiosClient = axios.create({});
const CancelToken = axios.CancelToken;
const isCancel = axios.isCancel;

export {
  type AxiosError,
  type AxiosRequestConfig,
  type CancelTokenSource,
  type AxiosProgressEvent,
  CancelToken,
  isCancel
};
// Define the structure of a retry queue item
interface RetryQueueItem {
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
  config: AxiosRequestConfig;
}

// Create a list to hold the request queue
const refreshAndRetryQueue: RetryQueueItem[] = [];

// Flag to prevent multiple token refresh requests
let isRefreshing = false;

// Add a response interceptor
axiosClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest: AxiosRequestConfig = error.config;
    /*
     * to not request for refresh JWT token add to post/get config this:
     * fetchOptions: {
     *   refreshToken: false
     * }
     */
    if (
      error.response &&
      error.response.status === 401 &&
      (originalRequest.fetchOptions as Record<string, any>)?.refreshToken !== false &&
      !isSessionForCodeActive()
    ) {
      if (!isRefreshing) {
        isRefreshing = true;

        try {
          // Refresh the access token
          await refreshAccessToken();

          // Retry all requests in the queue with the new token
          refreshAndRetryQueue.forEach(({ config, resolve, reject }) => {
            axios
              .request(config)
              .then((response) => resolve(response))
              .catch((err) => reject(err));
          });

          // Clear the queue
          refreshAndRetryQueue.length = 0;

          // Retry the original request
          return axiosClient(originalRequest);
        } catch (refreshError) {
          redirectToPublicPage(undefined, window.location.href);
        } finally {
          isRefreshing = false;
        }
      }

      return new Promise<void>((resolve, reject) => {
        refreshAndRetryQueue.push({ config: originalRequest, resolve, reject });
      });
    }

    // Return a Promise rejection if the status code is not 401
    return Promise.reject(error);
  }
);

/** Will set defaults to the global axios object, use only for headers that should be always present */
export function assignGlobalDefaultHeaders(headers: Record<string, string>) {
  Object.assign(axios.defaults.headers.common, headers);
}

export function isAxiosError<T>(result: unknown | AxiosError<T>): result is AxiosError<T> {
  if (!result) {
    return false;
  }
  return typeof result === 'object' && 'isAxiosError' in result;
}
